import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, FileSearch, Mail, UserPlus, UserCircle, Users, CirclePlus, X } from 'lucide-react';
import {
  certWeeklyHighlight, certCsCgTracking, certDailyReport,
  certLogoOverview, certCharts, certGetLayout,
  certGetEmailPreview, certDownloadEmailDraft, certGetRecipients, certSaveRecipients,
} from '../../services/reportingHubApi';
import { decodedToken } from '../../utils/auth';
import permissionConfig from '../../utils/permission.json';
import { renderWidget, Table } from './reportWidgets';

// statusColor + Table are imported from ./reportWidgets (shared).

// ── Daily report widget renderers live in ./reportWidgets ──────────

// All certification widget renderers live in ./reportWidgets (renderWidget).

function WidgetSlot({ widget, ctx }) {
  return (
    <div>
      {renderWidget(widget.widgetKey, { ...ctx, widgetName: widget.widgetName })}
    </div>
  );
}

function TemplateSection({ section, ctx }) {
  if (section.sectionKey === 'header' || !section.widgets || section.widgets.length === 0) return null;
  const full = section.widgets.filter((w) => w.side === 'full');
  const sided = section.widgets.filter((w) => w.side !== 'full').sort((a, b) => (a.side === 'left' ? -1 : 1));
  return (
    <div className="sm:mb-8 mb-2 border-b border-gray-400">
      <h2 className="text-base font-bold mb-1 pb-1">
        {section.sectionName}
      </h2>

      {/* ADD ONLY THIS */}
      <div className="pt-3 sm:pb-5 pb-2 px-2 sm:px-4 md:px-6 lg:px-8 xl:px-10">

        {full.map((w) => (
          <div key={w.widgetKey} className="mb-4">
            <WidgetSlot widget={w} ctx={ctx} />
          </div>
        ))}

        {sided.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {sided.map((w) => (
              <WidgetSlot
                key={w.widgetKey}
                widget={w}
                ctx={ctx}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

const CertificationReportView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { reportTypeKey, reportTypeLabel, year, projectId, project } = location.state || {};

  const now = new Date();
  const isoWeek = (() => {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  })();
  const [week, setWeek] = useState(isoWeek);
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [images, setImages] = useState({ trend: null, throughput: null, logo: null });
  const [imgBusy, setImgBusy] = useState({ charts: false, logo: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Email state
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [savedForm, setSavedForm] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ to: '', cc: '', bcc: '', subject: '' });
  const [modalErr, setModalErr] = useState('');
  const [modalBusy, setModalBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const bodyRef = useRef(null);
  const [searchParams] = useSearchParams();
  const reportType = searchParams.get("reportType");

  const user = decodedToken() || {};
  const syncTime = new Date().toLocaleString('en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).replace(',', '');
  const userName = user.userId || user.email || 'User';
  const canSendEmail =
    permissionConfig.SendEmailCertification?.[user.userType] === true &&
    reportTypeKey !== 'weekly_highlight';

  const isDaily = reportTypeKey === 'bcst_daily' || reportTypeKey === 'nbcst_daily';

  const titleSuffix = {
    bcst_daily: 'BCST Certification Daily Report',
    nbcst_daily: 'NBCST Certification Daily Report',
    weekly_highlight: `Weekly Highlight ${year}-WK${String(week).padStart(2, '0')}`,
    cs_cg_tracking: `CS and CG Tracking ${year}-WK${String(week).padStart(2, '0')}`,
  }[reportTypeKey] || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch the layout template; capture a clear flag if it's missing.
      let tpl = null;
      let tplMissing = false;
      try {
        const r = await certGetLayout(reportTypeKey);
        tpl = r.template;
      } catch (le) {
        tplMissing = le.response?.status === 404;
        if (!tplMissing) throw le;
      }

      let d = {};
      if (reportTypeKey === 'weekly_highlight') d = await certWeeklyHighlight(project);
      else if (reportTypeKey === 'cs_cg_tracking') d = await certCsCgTracking(project, year, week);
      else if (reportTypeKey === 'bcst_daily') d = await certDailyReport(project, 'BCST');
      else if (reportTypeKey === 'nbcst_daily') d = await certDailyReport(project, 'NBCST');

      setTemplate(tpl);
      setData(d);
      if (tplMissing && isDaily) {
        setError('Layout template not found for this report type. Run: node scripts/seedCertificationTemplates.js');
      }
    } catch (e) {
      setError(e.response?.status === 503 ? 'Source database unavailable.' : 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, [reportTypeKey, project, year, week, isDaily]);

  useEffect(() => { load(); }, [load]);

  // Generate only charts (Trend + Throughput) individually
  const generateChartsOnly = async () => {
    setImgBusy((prev) => ({ ...prev, charts: true }));
    try {
      const res = await certCharts(project, true);
      setImages((prev) => ({
        ...prev,
        trend: res.images?.trend || null,
        throughput: res.images?.throughput || null,
      }));
    } catch { /* ignore */ }
    finally { setImgBusy((prev) => ({ ...prev, charts: false })); }
  };

  // Generate only logo overview individually
  const generateLogoOnly = async () => {
    setImgBusy((prev) => ({ ...prev, logo: true }));
    try {
      const res = await certLogoOverview(project, true);
      setImages((prev) => ({ ...prev, logo: res.image || null }));
    } catch { /* ignore */ }
    finally { setImgBusy((prev) => ({ ...prev, logo: false })); }
  };

  // Email handlers
  const handleEmail = async () => {
    setEmailBusy(true);
    setEmailMsg('');
    try {
      const data = await certGetEmailPreview(project, reportTypeKey, year, week);
      setPreview(data);
      setEditSubject(data.subject || '');
    } catch (e) {
      if (e.response?.status === 409) setShowWarning(true);
      else setEmailMsg('Unable to build email preview.');
    } finally {
      setEmailBusy(false);
    }
  };

  const handleDownload = async () => {
    setDownloadBusy(true);
    try {
      const bodyHtml = bodyRef.current ? bodyRef.current.innerHTML : preview.bodyHtml;
      const res = await certDownloadEmailDraft(project, { bodyHtml, subject: editSubject, reportTypeKey });
      setEmailMsg(`Draft downloaded: ${res.fileName}`);
      setPreview(null);
    } catch {
      setEmailMsg('Unable to download draft.');
    } finally {
      setDownloadBusy(false);
    }
  };

  const openRecipientModal = async () => {
    setModalErr('');
    setShowWarning(false);
    let initial = { to: '', cc: '', bcc: '', subject: `${project} ${titleSuffix}` };
    try {
      const data = await certGetRecipients(project);
      if (data?.recipients) {
        const r = data.recipients;
        initial = { to: r.to || '', cc: r.cc || '', bcc: r.bcc || '', subject: r.subject || `${project} ${titleSuffix}` };
      }
    } catch { /* 404 ok */ }
    setForm(initial);
    setSavedForm(initial);
    setShowModal(true);
  };

  const saveRecipientList = async () => {
    if (!form.to.trim()) { setModalErr('The "To" field must include at least one recipient.'); return; }
    setModalBusy(true);
    setModalErr('');
    try {
      await certSaveRecipients(project, form);
      setSavedForm(form);
      setShowModal(false);
      setEmailMsg('Recipient list saved.');
    } catch (e) {
      setModalErr(e.response?.data?.error || 'Failed to save recipients.');
    } finally {
      setModalBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <Loader2 className="sm:w-15 sm:h-15 w-6 h-6 animate-spin text-blue-800" />
    </div>;
  }

  const ctx = { product: 'Certification', data, images, project, onGenerateCharts: generateChartsOnly, onGenerateLogo: generateLogoOnly, imgBusy };
  const isTemplateDriven = isDaily || reportTypeKey === 'weekly_highlight' || reportTypeKey === 'cs_cg_tracking';

  // Non-daily reports keep their dedicated (non-widget) rendering.
  const renderNonDaily = () => {
    if (reportTypeKey === 'weekly_highlight') {
      const rows = data?.rows || [];
      if (rows.length === 0) {
        return (
          <div className="text-center py-16">
            <FileSearch className="w-12 h-12 mx-auto text-gray-500 mb-3" />
            <div className="text-xl font-semibold">No highlight issues at this time</div>
            <div className="text-sm text-gray-400 mb-4">All items are on track based on the latest data.</div>
           <button onClick={() => navigate(`/reporting-hub/certification/search?type=${reportTypeKey}`)} className="px-4 py-2 bg-blue-600 hover:bg-blue-500">Go back to filters</button>
          </div>
        );
      }
      return (
        <div>
          <h2 className="text-base font-bold mb-3 border-b border-gray-700 pb-1">Highlight issues</h2>
          <Table columns={['Certification', 'Target CS', 'Status', 'Highlight', 'Remark']} keys={['certification', 'targetCs', 'status', 'highlight', 'remark']} rows={rows} />
        </div>
      );
    }
    // cs_cg_tracking
    return (
      <div className="space-y-6">
        <div><h2 className="text-base font-bold mb-2 border-b border-gray-700 pb-1">CG delay</h2>
          <Table columns={['Certification', 'Status', 'Target CS', 'Actual CS', 'Target CG', 'Actual CG', 'Remark']} keys={['certification', 'status', 'targetCs', 'actualCs', 'targetCg', 'actualCg', 'remark']} rows={data?.cgDelay} empty="No delay items this week" /></div>
        <div><h2 className="text-base font-bold mb-2 border-b border-gray-700 pb-1">CS delay</h2>
          <Table columns={['Certification', 'Scope', 'Status', 'Target CS', 'Target CG', 'Coverage', 'Remark']} keys={['certification', 'scope', 'status', 'targetCs', 'targetCg', 'coverage', 'remark']} rows={data?.csDelay} empty="No delay items this week" /></div>
        <div><h2 className="text-base font-bold mb-2 border-b border-gray-700 pb-1">CS this week</h2>
          <Table columns={['Certification', 'Scope', 'Status', 'Coverage', 'Target CS', 'Target CG', 'Remark']} keys={['certification', 'scope', 'status', 'coverage', 'targetCs', 'targetCg', 'remark']} rows={data?.csThisWeek} empty="No CS items this week" /></div>
        <div><h2 className="text-base font-bold mb-2 border-b border-gray-700 pb-1">Summary</h2>
          <Table columns={['Category', 'Total', 'Certified', 'Pretest', 'LAB']} keys={['category', 'total', 'certified', 'pretest', 'lab']} rows={data?.summary} /></div>
      </div>
    );
  };

  const showWeekControls = false;

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-6 overflow-x-hidden">
      <div className="w-full max-w-full mx-auto">

        <div className="border-b border-gray-400 pb-4 mb-4 sm:mb-6 mt-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 -ml-2">

            {/* LEFT SECTION */}
            <div className="flex items-center gap-1 min-w-0 sm:-ml-4 -ml-2">
              <button
                // onClick={() => navigate(-1)}
                   onClick={() => navigate("/reporting-hub/certification", {
                      state: location.state,
                    })}
                className="p-2 rounded hover:bg-gray-800 shrink-0"
                aria-label="Go back"
              >
                <ArrowLeft className="w-6 h-6 sm:w-6 sm:h-6 font-bold text-white font-bold" />
              </button>

              <h1 className="font-bold text-sm sm:text-xl md:text-2xl lg:text-2xl">
                {project} {titleSuffix}
              </h1>
            </div>

            {/* RIGHT CONTENT */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between lg:justify-end gap-4 sm:gap-6">

              {/* EMAIL ACTIONS */}
              <div className="flex items-center gap-3">

                {canSendEmail && (
                <button
                  onClick={handleEmail}
                  disabled={emailBusy}
                  className="flex items-center gap-2 px-3 py-2 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50 transition-colors text-xs sm:text-sm font-medium whitespace-nowrap"
                >
                  {emailBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  <span>Send Email</span>
                </button>
                )}

                {/* Recipient Button */}
                {canSendEmail && (
                <div className="relative group">
                  <button
                    onClick={openRecipientModal}
                    className="p-2 rounded hover:bg-gray-800 text-gray-300 flex items-center gap-1"
                  >
                    <Users className="w-5 h-5" />
                    <CirclePlus className="w-5 h-5 text-yellow-500" />
                  </button>

                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                    <div className="relative px-2 py-1 text-xs text-white bg-black border border-gray-500 rounded-md whitespace-nowrap shadow-lg">
                      Edit recipient

                      <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-gray-500" />
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-[-1px] w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-black" />
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* USER + SYNC */}
              {/* <div className="flex flex-col items-start sm:items-end "> */}
              <div className="flex flex-col items-start sm:items-end -mt-1 sm:-mt-5">
                <div className="text-[13px] sm:text-xs text-white text-left sm:text-right leading-tight">
                  <div>Last successful sync time:</div>
                  <div className="text-white">
                    {syncTime}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <UserCircle className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400" />
                  <span className="text-xs sm:text-sm text-white max-w-[140px]">
                    {userName}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>

        {emailMsg && <div className="mb-4 text-xs text-gray-300">{emailMsg}</div>}
        {template && (
          <div
            className="
      text-[10px]
      sm:text-xs
      md:text-sm
      text-gray-300
      mb-3 sm:mb-4
      break-words
      leading-relaxed
    "
          >
            Template: {template.name} · {template.templateType} · v{template.version}
          </div>
        )}
        {error && <div className="bg-red-900/40 border border-red-700 text-red-200  px-4 py-3 mb-4">{error}</div>}

        {isTemplateDriven ? (
          template?.sections
            ? template.sections.slice().sort((a, b) => a.position - b.position).map((s) => <TemplateSection key={s.sectionKey} section={s} ctx={ctx} />)
            : <div className="text-gray-500">No template found. Seed the certification templates first.</div>
        ) : renderNonDaily()}

        {/* Email preview modal */}
        {preview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4">
            <div
              className="
                bg-white
                text-black
                w-full
                max-w-full
                max-h-[100vh]
                flex
                flex-col
                overflow-hidden
              "
            >
              {/* Header */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-black">
                <h3 className="text-base sm:text-lg md:text-2xl font-bold text-white">
                  Download Draft Email — Preview (.eml file)
                </h3>

                <button
                  onClick={() => setPreview(null)}
                  className="
                    text-gray-500
                    hover:text-black
                    p-1
                    
                  "
                >
                  <X className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="px-3 sm:px-5 py-3 overflow-y-auto flex-1">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm mb-4 min-w-[600px]">
                    <tbody>
                      {[
                        ['To', preview.to],
                        ['Cc', preview.cc],
                        ['Bcc', preview.bcc],
                      ].map(([label, val]) => (
                        <tr key={label} className="align-top">
                          <td className="py-2 pr-3 font-semibold text-gray-600 w-20 whitespace-nowrap">
                            {label}
                          </td>

                          <td className="py-2">
                            <div className="border border-gray-300 px-2 py-2 bg-gray-50 break-words">
                              {val || (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}

                      <tr className="align-top">
                        <td className="py-2 pr-3 font-semibold text-gray-600 w-20 whitespace-nowrap">
                          Subject
                        </td>

                        <td className="py-2">
                          <input
                            value={editSubject}
                            onChange={(e) =>
                              setEditSubject(e.target.value)
                            }
                            className="
                              w-full
                              border border-gray-300
                              px-3 py-2
                              outline-none
                              focus:ring-2
                              focus:ring-blue-500
                            "
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Body Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                  <span className="text-sm font-semibold text-gray-600">
                    Email body
                  </span>

                  <span className="text-xs text-gray-400 font-bold">
                    Editable — click in the body to change text.
                    Wide tables/charts scroll sideways.
                  </span>
                </div>

                {/* Editable Body */}
                <div
                  ref={bodyRef}
                  contentEditable
                  suppressContentEditableWarning
                  className="
                    border border-gray-300 
                    p-3
                    bg-white
                    outline-none
                    focus:ring-2
                    focus:ring-blue-400
                    overflow-x-auto
                  "
                  style={{ minHeight: '250px' }}
                  dangerouslySetInnerHTML={{
                    __html: preview.bodyHtml,
                  }}
                />
              </div>

              {/* Footer Buttons */}
              <div
                className="
                  border-t border-gray-200
                  px-2 sm:px-2
                  py-5 
                  bg-black
                "
              >
                <div
                  className="
                    flex
                    flex-col
                    sm:flex-row
                    sm:justify-center
                    gap-2
                    sm:gap-30
                    bg-black
                  
                  "
                >
                  <button
                    onClick={() => {
                      setPreview(null);
                      openRecipientModal();
                    }}
                    className="
                      w-full
                      sm:w-64
                      px-4 py-2
                      bg-gray-200
                      hover:bg-gray-300
                      text-black 
                      font-semibold
                      
                    "
                  >
                    Edit Recipient
                  </button>

                  <button
                    onClick={() => setPreview(null)}
                    className="
                      w-full
                      sm:w-64
                      px-4 py-2
                      bg-gray-200
                      hover:bg-gray-300
                      text-black
                      font-semibold
                
                    "
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleDownload}
                    disabled={downloadBusy}
                    className="
                      w-full
                      sm:w-64
                      px-4 py-2
                      bg-blue-600
                      hover:bg-blue-500
                      text-white
                      disabled:opacity-50
                      font-semibold
                    
                    "
                  >
                    {downloadBusy
                      ? 'Preparing...'
                      : 'Download Draft (.eml)'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Warning: no recipients configured */}
        {showWarning && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-gray-900 border border-gray-700 p-6 max-w-md w-full mx-4 text-center">
              <h3 className="text-lg font-semibold mb-3">Missing recipients</h3>
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-gray-300 mb-6">No default recipient list found. Please set up recipients first by clicking Edit recipient.</p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setShowWarning(false)} className="px-4 py-2  bg-gray-600 hover:bg-gray-500">Cancel</button>
                <button onClick={openRecipientModal} className="px-4 py-2  bg-blue-600 hover:bg-blue-500">Go to Edit recipient</button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4">
            <div
              className="
    w-full
    max-h-[100vh]
    overflow-y-auto
    bg-gray-900
    border border-gray-700
    p-4 sm:p-6
  "
            >
              {/* Header */}
              <div className="flex items-start justify-between border-b border-gray-500 pb-3 mb-4">
                <div className="pr-3">
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white">
                    Edit Email Recipient
                  </h3>

                  <p className="text-sm sm:text-base md:text-lg text-white font-semibold mt-1">
                     Model: {project}
                  </p>
                </div>

                <button
                  onClick={() => setShowModal(false)}
                  className="
            text-gray-300
            hover:text-white
            hover:bg-gray-800
            p-1.5
            rounded
            transition-colors
            flex-shrink-0
          "
                  aria-label="Close"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Instructions */}
              <ul className="text-xs sm:text-sm md:text-base text-gray-300 list-disc pl-5 space-y-1 mb-4">
                <li>
                  Supports Outlook-style input. Paste multiple recipients using
                  semicolon (;).
                </li>
                <li>
                  "To" field must include at least one recipient.
                </li>
                <li>
                  CC and BCC are optional.
                </li>
                <li>
                  Subject is required.
                </li>
              </ul>


              <div className="px-0 sm:px-2 md:px-8 lg:px-10 xl:px-10 sm:-ml-5">
                {[
                  {
                    key: 'to',
                    label: 'To',
                    rows: 4,
                    ph: 'Name <a@x.com>; Other <b@y.com>',
                  },
                  {
                    key: 'cc',
                    label: 'CC',
                    rows: 2,
                    ph: 'optional',
                  },
                  {
                    key: 'bcc',
                    label: 'BCC',
                    rows: 2,
                    ph: 'optional',
                  },
                ].map((f) => (
                  <div
                    key={f.key}
                    className="
        mb-6
        flex
        flex-col
        md:flex-row
        md:items-center
        md:gap-6
      "
                  >
                    <label
                      className="
          w-full
          md:w-20
          lg:w-24
          text-sm
          sm:text-base
          font-medium
          text-white
          mb-2
          md:mb-0
          md:text-right
          flex-shrink-0
        "
                    >
                      {f.label} :
                      {f.key === 'to' && (
                        <span className="text-red-400"> *</span>
                      )}
                    </label>

                    <textarea
                      rows={f.rows}
                      value={form[f.key]}
                      placeholder={f.ph}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          [f.key]: e.target.value,
                        })
                      }
                      className="
          flex-1
          w-full
          bg-gray-800
          border border-gray-700
          px-3 py-2
          text-sm sm:text-base
          text-white
          outline-none
          focus:ring-2
          focus:ring-blue-500
        "
                    />
                  </div>
                ))}

                {/* Subject */}
                <div
                  className="
      mb-6
      flex
      flex-col
      md:flex-row
      md:items-center
      md:gap-6
    "
                >
                  <label
                    className="
        w-full
        md:w-20
        lg:w-24
        text-sm
        sm:text-base
        font-medium
        text-white
        mb-2
        md:mb-0
        md:text-right
        flex-shrink-0
      "
                  >
                    Subject :
                    <span className="text-red-400"> *</span>
                  </label>

                  <input
                    value={form.subject}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        subject: e.target.value,
                      })
                    }
                    className="
        flex-1
        w-full
        bg-gray-800
        border border-gray-700
        px-3 py-2
        text-sm sm:text-base
        text-white
        outline-none
        focus:ring-2
        focus:ring-blue-500
      "
                  />
                </div>
              </div>


              {/* Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-center gap-2 sm:gap-25 mt-5">
                <button
                  onClick={saveRecipientList}
                  disabled={modalBusy}
                  className="
      w-full sm:w-50
      px-4 py-2
      bg-blue-600
      hover:bg-blue-500
      disabled:opacity-50
      text-sm sm:text-base
      rounded-md
      text-center
    "
                >
                  {modalBusy ? 'Saving...' : 'Save as project default'}
                </button>

                <button
                  onClick={() => savedForm && setForm(savedForm)}
                  className="
      w-full sm:w-50
      px-4 py-2
      bg-gray-700
      hover:bg-gray-600
      text-sm sm:text-base
      rounded-md
      text-center
    "
                >
                  Reset to last saved
                </button>

                <button
                  onClick={() => setShowModal(false)}
                  className="
      w-full sm:w-50
      px-4 py-2
      bg-gray-700
      hover:bg-gray-600
      text-sm sm:text-base
      rounded-md
      text-center
    "
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CertificationReportView;
